'use client';

import React from 'react';
import { useGetMainOriginalsListQuery } from 'src/store/server/main/new/main.query';
import { checkLogin } from 'src/utils/auth';
import styled from 'styled-components';
import { Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { SkeletonAnimation } from '@/styles/animation';

import MyPickCard from './MyPickCard/MyPickCard';

export const MYPICK_CARD_COLORS = [
  '#F6F7F9', // 기본 그레이
  '#FBF4FF', // 연한 퍼플
  '#F7FCF1', // 연한 그린
  '#F2F9FE', // 연한 블루
  '#F7F4FF', // 연한 바이올렛
  '#FFF9F6', // 연한 코랄
  '#FFF9EB', // 연한 옐로우
  '#F4F7FF', // 연한 스카이블루
  '#FFF7FE', // 연한 핑크
] as const;

const MyPickSwiper = () => {
  const {
    data: originalsList,
    isLoading: isOriginalsListLoading,
    isError: isOriginalsListError,
  } = useGetMainOriginalsListQuery();

  // originalsList 중에서 myFavorite가 Y인 것을 추려내고, favoriteDateTime 기준으로 정렬
  const myPickOriginalsList = originalsList
    ?.filter((originals) => originals.myFavorite === 'Y')
    .sort((a, b) => {
      // favoriteDateTime이 더 최근인 항목이 앞으로 오도록 내림차순 정렬
      return (b.favoriteDateTime ?? 0) - (a.favoriteDateTime ?? 0);
    });

  /**
   * 선호하는 브랜드 숫자에 따른 조건부 렌더링
   */
  // 0. 로딩 시...
  if (isOriginalsListLoading || isOriginalsListError) {
    return <Skeleton />;
  }

  // 1. 미로그인 시 또는 선호하는 브랜드가 없을 경우
  if (!checkLogin() || myPickOriginalsList?.length === 0) {
    return (
      <EmptyGuideMessage>
        배너의 ♡를 누르면
        <br />
        나만의 Pick 목록을 만들 수 있어요
      </EmptyGuideMessage>
    );
  }

  // 2. 만약 선호하는 브랜드가 한 개일 경우
  if (myPickOriginalsList?.length === 1) {
    return (
      <MyPickCard
        originalsIdx={myPickOriginalsList[0].idx}
        pickImage={myPickOriginalsList[0].pickImage}
        pickName={myPickOriginalsList[0].pickName}
        linkUrl={myPickOriginalsList[0].linkUrl}
        backgroundColor={MYPICK_CARD_COLORS[0]}
      />
    );
  }

  // 3. 기본: 선택한 브랜드가 여러개일 경우
  return (
    <CustomSwiper
      modules={[Pagination]}
      slidesPerView={'auto'}
      spaceBetween={10}
      pagination={{
        clickable: true,
      }}
    >
      {myPickOriginalsList?.map((myPickOriginals, index) => (
        <SwiperSlide key={myPickOriginals.idx}>
          <MyPickCard
            originalsIdx={myPickOriginals.idx}
            pickImage={myPickOriginals.pickImage}
            pickName={myPickOriginals.pickName}
            linkUrl={myPickOriginals.linkUrl}
            backgroundColor={MYPICK_CARD_COLORS[index % MYPICK_CARD_COLORS.length]}
          />
        </SwiperSlide>
      ))}
    </CustomSwiper>
  );
};

export default MyPickSwiper;

const EmptyGuideMessage = styled.div`
  display: flex;
  width: 100%;
  height: 90px;
  margin-top: 10px;
  padding: 19px 35px;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  background: #f6f7f9;
  text-align: center;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%;
`;

const CustomSwiper = styled(Swiper)`
  position: relative;
  width: 100%;

  @media (min-width: 280px) {
    .swiper-slide {
      width: 240px;
    }
  }
`;

// Skeleton UI
const Skeleton = styled.div`
  ${SkeletonAnimation};
  width: 100%;
  height: 90px;
  margin-top: 10px;
  border-radius: 10px;
`;
